import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  QueryList,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  TemplateRef,
  EmbeddedViewRef } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';
import { ValidationErrors } from '@angular/forms';

import { DistributionList } from '../users/distribution-list.model';
import { DynamicFieldsDirective } from './dynamic-fields.directive';
import { FieldComponent } from './field.component';
import { FormField, Link } from './form-field.model';
import { TextFieldComponent } from './text-field.component';
import { AutosizeTextFieldComponent } from './autosize-text-field.component';
import { FilesFieldComponent } from './files-field.component';
import { DateFieldComponent } from './date-field.component';
import { TokensFieldComponent } from './tokens-field.component';
import { UsersFieldComponent } from './users-field.component';
import { EmailsFieldComponent } from './emails-field.component';
import { SenderFieldComponent } from './sender-field.component';
import { AutocompleteSelectFieldComponent } from './autocomplete-select-field.component';
import { JellyrollFoldersFieldComponent } from './jellyroll-folders-field.component';
import { JellyrollFilenameField } from './jellyroll-filename-field.component';
import { LandingPadsFieldComponent } from './landing-pads-field.component';
import { NrgTermsFieldComponent } from './nrg-terms-field.component';
import { CheckboxFieldComponent } from './checkbox-field.component';
import { TableFieldComponent } from './table-field.component';
import { TimecodeFieldComponent } from './timecode-field.component';
import { SelectFieldComponent } from './select-field.component';
import { JumboSelectFieldComponent } from './jumbo-select-field.component';
import { AutocompleteTokensFieldComponent } from './autocomplete-tokens-field.component';
import { UsersAndListsFieldComponent } from './users-and-lists-field.component';
import { EmailsAndListsFieldComponent } from './emails-and-lists-field.component';
import { DateHistoryFieldComponent } from './date-history-field.component';
import { RichTextFieldComponent } from './rich-text-field.component';
import { NumberFieldComponent } from './number-field.component';
import { IndexFieldComponent } from './index-field.component';
import { AutoIndexFieldComponent } from './auto-index-field.component';
import { ConvertFieldComponent } from './convert-field.component';
import { NotesFieldComponent } from './notes-field.component';
import { TimecodeStandardFieldComponent } from './timecode-standard-field.component';
import { OffsetFieldComponent } from './offset-field.component';
import { WorkflowValidations } from '../workflows/workflow.model';
import { TechSpecsFieldComponent } from './tech-specs-field.component';

interface RowLink {
  addOnly: boolean,
  id: string,
  fields?: string[]
}

@Component({
  selector: 'app-form-row',
  templateUrl: './form-row.component.html',
  styleUrls: ['./form-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormRowComponent implements OnInit {
  @Input() blank: boolean = true;
  @Input() distributionLists = [];
  @Input() draftData: any;
  @Input() fields: FormField[];
  @Input() focusOnLoad: boolean = false;
  @Input() id: string;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() dynamicOverlays;
  @Input() recipients = [];
  @Input() returnEnabled: boolean = true;
  @Input() templateData: any;
  @Input() workflowId: any;
  @Output() atLeastOneRequiredFieldAdded = new EventEmitter<{id: string, field: FieldComponent}>();
  @Output() blankChanged = new EventEmitter<FormRowComponent>();
  @Output() copyRow = new EventEmitter<null>();
  @Output() createFileSection = new EventEmitter<{fileId: string, section: any}>();
  @Output() disclaimerChanged = new EventEmitter<{field: string, value: any}>();
  @Output() dragDropped = new EventEmitter<string>();
  @Output() dragEnded = new EventEmitter<null>();
  @Output() dragStarted = new EventEmitter<null>();
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() listAdded = new EventEmitter<number>();
  @Output() removeRow = new EventEmitter<null>();
  @Output() removeFileSection = new EventEmitter<string>();
  @Output() resized = new EventEmitter<null>();
  @Output() returnKey = new EventEmitter<null>();
  @Output() rowLinkAdded = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() rowLinkRemoved = new EventEmitter<{field: string, id: string}>();
  @Output() rowLinkUpdated = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() showDisclaimer = new EventEmitter<any>();
  @Output() standardChanged = new EventEmitter<any>();
  @Output() standardConverted = new EventEmitter<any>();
  @Output() stickyHeaderAdded = new EventEmitter<any>();
  @Output() timecodesOffsetChanged = new EventEmitter<string>();
  @Output() undoableAction = new EventEmitter<any>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() valueChanged = new EventEmitter<{field: string, value: any, row: string}>();
  @Output() viewLoaded = new EventEmitter<FormRowComponent>();
  @ViewChild(DynamicFieldsDirective) dynamicFields: DynamicFieldsDirective;
  @ViewChild("versionElement") versionElement: ElementRef;
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;

  components: FieldComponent[] = [];
  copyable: boolean = false;
  draggable: boolean = false;
  errors: {type: string, id: string, label: string, errors: ValidationErrors}[] = [];
  focused: boolean = false;
  highlighted: boolean = false;
  highlightColor: string;
  dirty: boolean = false;
  links: RowLink[] = [];
  loaded: boolean = false;
  standard: any;
  targeted: boolean = false;
  touched: boolean = false;
  valid: boolean = false;
  value: any = {};
  versionView: EmbeddedViewRef<null>;
  versionStatus: string;
  viewsLoaded: number = 0;
  removable: boolean = false;
  workflowValidations: WorkflowValidations;

  private _disabled: boolean = false;
  get disabled() {return this._disabled}

  constructor(
    private cd: ChangeDetectorRef,
    private overlay: Overlay,
    private resolver: ComponentFactoryResolver) { }

  ngOnInit() {
    for (let field of this.fields) {
      this.loadField(field)
    }
  }

  onViewLoaded(instance) {
    this.viewsLoaded += 1
    if (this.viewsLoaded === this.components.length) {
      this.loaded = true;
      this.setupTimecodes();
      this.viewLoaded.emit(this);
      if (this.focusOnLoad) {
        setTimeout(() => { this.focus() }, 100);
      }
    }
  }

  validate() {
    this.valid = this.components.filter((f) => !f.valid).length == 0
    if (!this.valid) {
      this.errors = [];
      for (let field of this.components) {
        if (!field.valid && field.errors != null) {
          this.errors.push({
            type: 'field',
            id: field.id,
            label: field.label,
            errors: field.errorDescriptions
          });
        }
      }  
    }
  }

  reset() {
    for (let field of this.components) {field.reset()}
  }

  resetFiles() {
    for (let component of this.components) {
      if (component instanceof FilesFieldComponent) {
        component.reset();
      }
    }
  }

  checkDirty() {
    this.dirty = (this.components.filter((f) => f.dirty).length > 0)
  }

  resetDirty(templateData) {
    if (templateData != null) {
      for (let field of this.components) {
        let value = this.templateValueForField(templateData, field.id);
        field.resetDirty(value);
      }
    }
    this.checkDirty();
  }

  checkTouched() {
    this.touched = (this.components.filter((f) => f.touched).length > 0)
  }

  disable() {
    for (let field of this.components) {field.disable()}
    this._disabled = true;
  }

  enable() {
    for (let field of this.components) {field.enable()}
    this._disabled = false;
  }

  componentClassForFieldType(type): any {
    switch (type) {
    case "checkbox":
      return CheckboxFieldComponent;
    case "date":
      return DateFieldComponent;
    case "date_history":
      return DateHistoryFieldComponent;
    case "file_select":
      return FilesFieldComponent;
    case "textarea":
    case "text_area":
      return AutosizeTextFieldComponent;
    case "email_list":
      return EmailsAndListsFieldComponent;
    case "emails":
      return EmailsFieldComponent;
    case "email":
    case "sender":
    case "sender_email":
      return SenderFieldComponent;
    case "autocomplete_select":
      return AutocompleteSelectFieldComponent;
    case "select":
      return SelectFieldComponent;
    case "jumbo_select":
      return JumboSelectFieldComponent;
    case "user_search":
    case "user_list":
      return UsersAndListsFieldComponent;
    case "users":
      return UsersFieldComponent;
    case "jellyroll_folders":
    case "jellyroll_folder":
      return JellyrollFoldersFieldComponent;
    case "jellyroll_filename":
      return JellyrollFilenameField;
    case "landingpads":
      return LandingPadsFieldComponent;
    case "nrg":
      return NrgTermsFieldComponent;
    case "table":
      return TableFieldComponent;
    case "timecode":
      return TimecodeFieldComponent;
    case "timecode_standard":
      return TimecodeStandardFieldComponent;
    case "autocomplete_list":
      return AutocompleteTokensFieldComponent;
    case "list":
      return TokensFieldComponent;
    case "rich_text":
      return RichTextFieldComponent;
    case "number":
      return NumberFieldComponent;
    case "index":
      return IndexFieldComponent;
    case "auto_index":
      return AutoIndexFieldComponent;
    case "convert":
      return ConvertFieldComponent;
    case "offset":
      return OffsetFieldComponent;
    case "notes":
      return NotesFieldComponent;
    case "tech_specs":
      return TechSpecsFieldComponent;
    default:
      return TextFieldComponent;
    }
  }

  loadField(field: FormField, userInitiated: boolean = false) {
    let componentClass = this.componentClassForFieldType(field.type);
    if (componentClass == null) {return};

    let factory = this.resolver.resolveComponentFactory(componentClass);
    let container = this.dynamicFields.viewContainerRef;
    let component = container.createComponent(factory);

    let element = component.location.nativeElement
    element.className = "form-field " + this.columnForField(field);

    let instance = <FieldComponent>component.instance
    instance.viewLoaded.subscribe(() => this.onViewLoaded(instance));
    instance.field = field;
    instance.locked = this.locked;
    instance.lockValues = this.lockValues;
    instance.workflowId = this.workflowId;
    instance.focusChanged.subscribe((event) => {
      this.updateFocus(event);
    })
    instance.focusTabbed.subscribe((event) => this.focusNext(event) )

    instance.valueChanged.subscribe((event) => {
      this.value[event.field] = event.value;
      this.checkDirty();
      this.checkTouched();
      this.validate();
      event.row = this.id;
      if (this.loaded) {
        if (this.blank && event.value != null) {
          this.blank = false;
          this.blankChanged.emit(this);
          for (let component of this.components) {
            component.updateLinkedRows();
          }
        }
      }
      this.valueChanged.emit(event)
      for (let link of this.links) {
        if (!link.addOnly) {
          this.updateRowLink(link);
        }
      }
    });
    instance.updateLinked.subscribe((event) => {
      this.onUpdateLinked(event, true);
    })
    instance.highlightChanged.subscribe((event) => {
      this.onHighlightChanged();
    })
    instance.returnKey.subscribe((field) => this.returnKey.emit())
    instance.resized.subscribe((field) => this.resized.emit())
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.undoableAction.subscribe((action) => this.undoableAction.emit(action))

    let fieldId = instance.id != null ? instance.id : field.id;

    if (this.templateData != null) {
      instance.templateData = this.templateValueForField(this.templateData, fieldId)
    }

    if (this.draftData != null) {
      instance.draftData = this.templateValueForField(this.draftData, fieldId)
    }

    if (this.workflowValidations != null) {
      if (this.workflowValidations.atLeastOneRequired != null) {
        for (let group of this.workflowValidations.atLeastOneRequired) {
          for (let field of group) {
            if (fieldId == field) {
              this.atLeastOneRequiredFieldAdded.emit({id: fieldId, field: instance});
            }
          }
        }
      }
    }

      switch (field.type) {
      case "file_select":
        let files = instance as FilesFieldComponent;
        files.createFileSection.subscribe((event) => {
          this.createFileSection.emit(event)
        })
        files.removeFileSection.subscribe((event) => {
          this.removeFileSection.emit(event)
        })
        break;
      case "textarea":
        let autosizeText = instance as AutosizeTextFieldComponent;
        autosizeText.returnEnabled = this.returnEnabled;
        break;
      case "autocomplete_select":
        let autoselect = instance as AutocompleteSelectFieldComponent;
        autoselect.disclaimerChanged.subscribe((event) => this.disclaimerChanged.emit(event));
        autoselect.showDisclaimer.subscribe((event) => this.showDisclaimer.emit(event));
        break;
      case "select":
        let select = instance as SelectFieldComponent;
        select.disclaimerChanged.subscribe((event) => this.disclaimerChanged.emit(event));
        select.showDisclaimer.subscribe((event) => this.showDisclaimer.emit(event));
        break;
      case "timecode":
        let timecode = instance as TimecodeFieldComponent;
        timecode.setStandard(this.standard != null ? this.standard : null);
        timecode.timecodeChanged.subscribe((event) => this.updateTimecodes(event.field, event.value));
        break;
      case "timecode_standard":
        let standard = instance as TimecodeStandardFieldComponent;
        standard.standardChanged.subscribe((event) => this.standardChanged.emit(event));
        break;
      case "convert":
        let convert = instance as ConvertFieldComponent;
        convert.standardConverted.subscribe((event) => this.standardConverted.emit(event));
        break;
      case "offset":
        let offset = instance as OffsetFieldComponent;
        offset.timecodesOffsetChanged.subscribe((event) => this.timecodesOffsetChanged.emit(event));
        break;
      case "table":
        let table = instance as TableFieldComponent;
        table.standard = this.standard;
        table.disclaimerChanged.subscribe((event) => this.disclaimerChanged.emit(event));
        table.listAdded.subscribe((event) => this.listAdded.emit(event));
        table.rowLinkAdded.subscribe((event) => this.onRowLinkAdded(event, true))
        table.rowLinkRemoved.subscribe((event) => this.onRowLinkRemoved(event, true))
        table.rowLinkUpdated.subscribe((event) => this.onRowLinkUpdated(event, true))
        table.stickyHeaderAdded.subscribe((header) => this.stickyHeaderAdded.emit(header))
        break;
      case "user_search":
      case "user_list":
        let usersList = instance as UsersAndListsFieldComponent;
        usersList.listAdded.subscribe((event) => this.listAdded.emit(event));
        usersList.distributionLists = this.distributionLists;
        usersList.recipients = this.recipients;
        break;
      case "email_list":
        let emailList = instance as EmailsAndListsFieldComponent;
        emailList.listAdded.subscribe((event) => this.listAdded.emit(event));
        emailList.distributionLists = this.distributionLists;
        break;
      }

    this.components.push(instance);
  }

  templateValueForField(templateData, fieldId) {
    fieldId = (''+fieldId).toLowerCase();
    for (let key in templateData) {
      let id = key.toLowerCase();
      if (id == fieldId) {
        return templateData[key];
      }
    }
  }

  focus() {
    for (let component of this.components) {
      if (!component.locked) {
        component.focus();
        return;
      }
    }
  }

  focusNext(component: FieldComponent) {
    let current = this.components.indexOf(component);
    if (current < this.components.length) {
      this.components[current+1].focus();
    }
  }

  blur() {
    for (let component of this.components) {
      component.blur();
    }
  }

  columnForField(field): string {
    if (field.columns != null) {
      let width = field.columns === 0.5 ? 'half' : field.columns;
      return 'column-'+width;
    } else {
      return 'column-rigid';
    }
  }

  updateLinkedFields() {
    for (let component of this.components) {
      component.updateLinkedFields();
      component.updateLinkedRows();
    }
  }

  onUpdateLinked(event: {link: Link, value?: any, categories?: any[], enabled?: boolean}, propogate=false) {
    let link = event.link
    if (link.row) {
      if (!this.blank) {
        if (event.enabled) {
          this.addRowLink(link)
        } else if (!link.addOnly) {
          this.removeRowLink(link)
        }
      }
    } else {
      let component = this.components.find((c) => c.id === link.id);
      if (component != null) {
        if (event.hasOwnProperty('value')) {
          if (link.pattern != null) {
            component.setPatternValue(link.pattern, event.value);
          } else {
            component.setValue(event.value, true)
          }
        } else if (event.hasOwnProperty('categories')) {
          component.setCategories(event.categories)
        } else if (event.hasOwnProperty('enabled')) {
          if (event.enabled) {
            component.enable();
          } else {
            component.disable();
          }
        }
      } else if (propogate) {
        this.updateLinked.emit(event);
      }
    }
  }

  addRowLink(link) {
    let existing = this.links.find((l) => l.id == link.id)
    if (existing == null) {
      this.links.push({
        addOnly: link.addOnly,
        id: link.id,
        fields: link.fields
      })
      let value: any = {"_id": this.id};
      if (link.fields) {
        for (let field of link.fields) {
          value[field] = this.value[field];
        }
      } else {
        for (let key in this.value) {
          value[key] = this.value[key];
        }
      }
      this.rowLinkAdded.emit({field: link.id, id: this.id, value: value})
    }
  }

  updateRowLink(link) {
    let value: any = {"_id": this.id};
    if (link.fields) {
      for (let field of link.fields) {
        value[field] = this.value[field];
      }
    } else {
      for (let key in this.value) {
        value[key] = this.value[key];
      }
    }
    this.rowLinkUpdated.emit({field: link.id, id: this.id, value: value})
  }

  removeRowLink(link) {
    let index = this.links.findIndex((l) => l.id == link.id)
    if (index != -1) {
      this.links.splice(index,1);
      this.rowLinkRemoved.emit({field: link.id, id: this.id})
    }
  }

  onRowLinkAdded(event: {field: string, id: string, value: any}, propogate=false) {
    let component = this.components.find((c) => c.id === event.field && c instanceof TableFieldComponent) as TableFieldComponent;
    if (component != null) {
      component.addLinkedRow(event.id, event.value)
    } else if (propogate) {
      this.rowLinkAdded.emit(event)
    }
  }

  onRowLinkRemoved(event: {field: string, id: string}, propogate=false) {
    let component = this.components.find((c) => c.id === event.field && c instanceof TableFieldComponent) as TableFieldComponent;
    if (component != null) {
      component.removeLinkedRow(event.id)
    } else if (propogate) {
      this.rowLinkRemoved.emit(event)
    }
  }

  onRowLinkUpdated(event: {field: string, id: string, value: any}, propogate=false) {
    let component = this.components.find((c) => c.id === event.field && c instanceof TableFieldComponent) as TableFieldComponent;
    if (component != null) {
      component.updateLinkedRow(event.id, event.value)
    } else if (propogate) {
      this.rowLinkUpdated.emit(event)
    }
  }

  onHighlightChanged() {
    let highlighted = false;
    let colors = ["red", "yellow"];
    let color: string;
    for (let component of this.components) {
      if (component.highlighted) {
        highlighted = true
        if (color == null || colors.indexOf(color) > colors.indexOf(component.highlightColor)) {
          color = component.highlightColor;
        }
      }
    }
    this.highlighted = highlighted;
    this.highlightColor = color;
    this.highlightChanged.emit({highlighted: highlighted, color: color});
  }

  updateFocus(focused: boolean, emit: boolean = true) {
    this.focused = focused;
    if (emit) {
      this.focusChanged.emit(focused);
    }
  }

  setStandard(event: {standard: any, value: any}) {
    this.standard = event.standard;
    for (let component of this.components) {
      switch (component.field.type) {
      case 'convert':
        let convert = component as ConvertFieldComponent;
        convert.setStandard(event.standard);
        convert.setValue(event.value);
        break;
      case 'offset':
        let offset = component as OffsetFieldComponent;
        offset.setStandard(event.standard);
        break;
      case 'timecode_standard':
        let timecodeStandard = component as TimecodeStandardFieldComponent;
        timecodeStandard.setValue(event.value);
        break;
      }
    }
    this.updateTimecodes("standard", event);
  }

  convertStandard(event: {standard: any, value: any}) {
    this.standard = event.standard;
    for (let component of this.components) {
      if (component.field.type == 'timecode_standard') {
        let timecodeStandard = component as TimecodeStandardFieldComponent;
        timecodeStandard.setValue(event.value);
      }
    }
    this.updateTimecodes("convert", event);
  }

  offsetTimecodesBy(event: {offset: string, subtract: boolean}) {
    this.updateTimecodes("offset", event);
  }

  setupTimecodes() {
    let timecodeIn = this.components.find((c) => c.field.id === "timecode_in") as TimecodeFieldComponent
    let timecodeOut = this.components.find((c) => c.field.id === "timecode_out") as TimecodeFieldComponent
    if (timecodeIn != null && timecodeOut != null) {
      timecodeOut.setMinTimecode(timecodeIn.value);
    }
  }

  updateTimecodes(field: string, value: any) {
    let timecodeIn = this.components.find((c) => c.field.id === "timecode_in") as TimecodeFieldComponent
    let timecodeOut = this.components.find((c) => c.field.id === "timecode_out") as TimecodeFieldComponent
    let duration = this.components.find((c) => c.field.id === "duration") as TimecodeFieldComponent
    let timecodes = [timecodeIn, timecodeOut, duration].filter((t) => t != null);

    if (field === "standard") {
      for (let timecode of timecodes) {
        timecode.setStandard(value.standard)
      }
      if (timecodes.length == 3) {
        if (!timecodeIn.valid || !timecodeOut.valid) {
          duration.setValue(null);
        }
      }
      let tables = this.components.filter((c) => c.field.type === "table") as TableFieldComponent[]
      for (let table of tables) {
        table.setStandard(value)
      }
      return
    } else if (field === "convert") {
      for (let timecode of timecodes) {
        timecode.convertStandard(value.standard)
      }
      if (timecodeIn != null && timecodeOut != null) {
        timecodeOut.setMinTimecode(timecodeIn.value);
      }
      this.validate();
      for (let timecode of timecodes) {
        this.valueChanged.emit({field: timecode.id, value: timecode.value, row: this.id})
      }
      let tables = this.components.filter((c) => c.field.type === "table") as TableFieldComponent[]
      for (let table of tables) {
        table.convertStandard(value)
      }
    } else if (field == "offset") {
      let anchor = value.anchor;
      if (timecodeIn != null && timecodeOut != null) {
        if (timecodeIn.lessThan(anchor) && timecodeOut.greaterThan(anchor)) {
          // Anchor fall between timecodes, don't offset
          return;
        }
      } 
      for (let timecode of timecodes) {
        if (timecode.id != "duration") {
          timecode.offsetBy(value)
        }
      }
      if (timecodeIn != null && timecodeOut != null) {
        timecodeOut.setMinTimecode(timecodeIn.value);
      }
      this.validate();
      for (let timecode of timecodes) {
        this.valueChanged.emit({field: timecode.id, value: timecode.value, row: this.id})
      }
      let tables = this.components.filter((c) => c.field.type === "table") as TableFieldComponent[]
      for (let table of tables) {
        table.offsetTimecodesBy(value)
      }
    }

    // Ensure timecodeOut is always after timecodeIn
    if (field == "timecode_in" && timecodeOut != null) {
      timecodeOut.setMinTimecode(value);
    }

    // Auto calculate empty fields
    if (this.standard != null && timecodes.length == 3) {
      let inSet = !timecodeIn.blank && timecodeIn.valid;
      let outSet = !timecodeOut.blank && timecodeOut.valid;
      let durationSet = !duration.blank && duration.valid;
      switch (field) {
      case 'timecode_in':
      case 'timecode_out':
        if (inSet && outSet) {
          duration.calculateDuration(timecodeIn.value, timecodeOut.value);
        } else {
          duration.setValue(null);
        }
        break;
      case 'duration':
        if (durationSet) {
          if (inSet) {
            timecodeOut.calculateTimecodeOut(timecodeIn.value, duration.value);
          } else if (outSet){
            timecodeIn.calculateTimecodeIn(timecodeOut.value, duration.value);
          }
        }
        break;
      }
    }
  }

  setValue(value, emit = false) {
    if (value == null) { return }
    for (let key in value) {
      let field = this.components.find((c) => c.id === key)
      if (field != null) {
        field.setValue(value[key], emit)
        this.value[key] = value[key];
      }
    }
  }

  setPreviousValue(value) {
    if (value == null) { return }
    for (let field of this.components) {
      field.setPreviousValue(value[field.id]);
    }
  }

  resetPreviousValue() {
    for (let field of this.components) {
      field.resetPreviousValue();
    }
  }

  setVersionStatus(status) {
    this.versionStatus = status;
    switch (this.versionStatus) {
    case "new":
    case "removed":
      this.showVersionStatus();
      break;
    }
    this.cd.markForCheck();
  }

  resetVersionStatus() {
    this.versionStatus = null;
    this.hideVersionStatus();
    this.cd.markForCheck();
  }

  showVersionStatus() {
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
    for (let field of this.components) {
      field.positionOverlays();
    }
  }

  hideVersionStatus() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
  }

  setLockValues(state) {
    this.lockValues = state;
    for (let field of this.components) {
      field.setLockValues(state);
    }
  }
  
  onDragStart(event) {
    event.dataTransfer.setData('text', this.id)
    this.dragStarted.emit()
  }

  onDragEnd(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dragEnded.emit();
  }

  onDragOver(event, row) {
    event.preventDefault();
    event.stopPropagation();
    this.targeted = true;
  }

  onDragLeave(event, row) {
    event.preventDefault();
    event.stopPropagation();
    this.targeted = false;
  }

  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    let id = event.dataTransfer.getData('text');
    if (id != this.id) {
      this.dragDropped.emit(id)
    }
    this.targeted = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/forms/form-row.component.ts